import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Modal,
  Tabs,
  Badge,
  LoadingSkeleton,
} from '@nexora/design-system';
import { useToast } from '@nexora/design-system';
import {
  useBatches,
  useBatchTracking,
  useLotMovements,
  useSerialTracking,
  useCreateBatch,
  useCreateSerial,
} from './useInventory';

type TrackingTab = 'batches' | 'serials';

export function TrackingPage(): JSX.Element {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<TrackingTab>('batches');

  const [batchSearch, setBatchSearch] = useState('');
  const [serialSearch, setSerialSearch] = useState('');

  const [searchedBatch, setSearchedBatch] = useState('');
  const [searchedSerial, setSearchedSerial] = useState('');

  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);

  const [batchForm, setBatchForm] = useState({ productId: '', batchNumber: '', expirationDate: '' });
  const [serialForm, setSerialForm] = useState({ productId: '', serialNumber: '' });

  const {
    data: batchTracking,
    isLoading: batchLoading,
    error: batchError,
  } = useBatchTracking(searchedBatch);

  const {
    data: serialTracking,
    isLoading: serialLoading,
    error: serialError,
  } = useSerialTracking(searchedSerial);

  const createBatch = useCreateBatch();
  const createSerial = useCreateSerial();

  const handleSearchBatch = () => {
    setSearchedBatch(batchSearch.trim());
  };

  const handleSearchSerial = () => {
    setSearchedSerial(serialSearch.trim());
  };

  const handleCreateBatch = async () => {
    if (!batchForm.productId.trim() || !batchForm.batchNumber.trim()) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    try {
      await createBatch.mutateAsync({
        productId: batchForm.productId,
        batchNumber: batchForm.batchNumber,
        expirationDate: batchForm.expirationDate || undefined,
      });
      addToast('Lote registrado com sucesso', 'success');
      setBatchModalOpen(false);
      setBatchForm({ productId: '', batchNumber: '', expirationDate: '' });
    } catch {
      addToast('Erro ao registrar lote', 'error');
    }
  };

  const handleCreateSerial = async () => {
    if (!serialForm.productId.trim() || !serialForm.serialNumber.trim()) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }

    try {
      await createSerial.mutateAsync({
        productId: serialForm.productId,
        serialNumber: serialForm.serialNumber,
      });
      addToast('Número de série registrado com sucesso', 'success');
      setSerialModalOpen(false);
      setSerialForm({ productId: '', serialNumber: '' });
    } catch {
      addToast('Erro ao registrar número de série', 'error');
    }
  };

  const tabs = [
    { key: 'batches', label: 'Lotes' },
    { key: 'serials', label: 'Séries' },
  ];

  const [statusFilter, setStatusFilter] = useState('');
  const batchesQ = useBatches(statusFilter ? { status: statusFilter } : undefined);

  /* `BatchResponse` traz `productId`, nao o nome. Sem este mapa a coluna
     PRODUTO mostraria UUID — a fixture da captura escondia isso porque punha o
     nome no campo de id. Mesma solucao que o StockPage ja usa. */
  const productsQ = useQuery<{ id: string; name: string }[]>({
    queryKey: queryKeys.masterData.products(''),
    queryFn: async () => apiClient.get<{ id: string; name: string }[]>('/products'),
  });
  const productName = (id: string): string =>
    (productsQ.data ?? []).find((p) => p.id === id)?.name ?? id;
  const batchRows = batchesQ.data?.data ?? [];
  const counts = batchesQ.data?.counts;

  const STATUS_LABEL: Record<string, string> = {
    ACTIVE: 'Ativo',
    EXPIRED: 'Vencido',
    BLOCKED: 'Bloqueado',
  };
  const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'error'> = {
    ACTIVE: 'success',
    EXPIRED: 'warning',
    BLOCKED: 'error',
  };
  const fmtDate = (v: string | null): string =>
    v ? new Date(v).toLocaleDateString('pt-BR') : '\u2014';

  const SERIAL_LABEL: Record<string, string> = {
    AVAILABLE: 'Disponível',
    RESERVED: 'Reservada',
    SOLD: 'Vendida',
    DEFECTIVE: 'Defeituosa',
    RETURNED: 'Devolvida',
  };

  const MOVEMENT_LABEL: Record<string, string> = {
    RECEIVE: 'Entrada',
    ISSUE: 'Saída',
    TRANSFER_IN: 'Transf. entrada',
    TRANSFER_OUT: 'Transf. saída',
    ADJUSTMENT_IN: 'Ajuste (+)',
    ADJUSTMENT_OUT: 'Ajuste (−)',
    COUNT: 'Inventário',
  };
  const MOVEMENT_INBOUND = new Set(['RECEIVE', 'TRANSFER_IN', 'ADJUSTMENT_IN']);
  const MOVEMENT_OUTBOUND = new Set(['ISSUE', 'TRANSFER_OUT', 'ADJUSTMENT_OUT']);

  /* Historico do lote: kardex filtrado por `lotNumber`. Nao existe endpoint de
     "historico do lote" — e nem precisa: o movimento e o historico. */
  const lotMovementsQ = useLotMovements(searchedBatch);
  const lotMovements = lotMovementsQ.data?.data ?? [];

  return (
    <div className="track-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Rastreabilidade</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Rastreabilidade por lote e número de série.</p>
        </div>
      </div>

      <Tabs
        tabs={tabs}
        value={activeTab}
        onChange={(key) => setActiveTab(key as TrackingTab)}
      />

      {/* ── Lotes Tab ── */}
      {activeTab === 'batches' && counts && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-md mt-lg mb-lg">
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Lotes ativos
            </div>
            <div className="font-display-lg text-display-lg text-on-surface tabular-nums mt-xs">
              {counts.active}
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              A vencer em {batchesQ.data?.expiringInDays ?? 30} dias
            </div>
            <div
              className={`font-display-lg text-display-lg tabular-nums mt-xs ${
                counts.expiringSoon > 0 ? 'text-warning-amber' : 'text-on-surface'
              }`}
            >
              {counts.expiringSoon}
            </div>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="font-label-md text-label-md text-on-surface-variant uppercase">
              Bloqueados
            </div>
            <div
              className={`font-display-lg text-display-lg tabular-nums mt-xs ${
                counts.blocked > 0 ? 'text-error' : 'text-on-surface'
              }`}
            >
              {counts.blocked}
            </div>
            <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs mb-0">
              Retenção por qualidade
            </p>
          </div>
        </div>
      )}

      {activeTab === 'batches' && (
        <Card padding="0" className="mb-lg">
          <CardHeader
            className="px-md pt-md"
            action={
              <div className="flex items-center gap-sm">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filtrar por status"
                  className="bg-surface border border-outline-variant font-body-sm text-body-sm text-on-surface rounded py-1.5 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer"
                >
                  <option value="">Todos os status</option>
                  <option value="ACTIVE">Ativo</option>
                  <option value="EXPIRED">Vencido</option>
                  <option value="BLOCKED">Bloqueado</option>
                </select>
                <span className="font-label-md text-label-md text-on-surface-variant tabular-nums whitespace-nowrap">
                  {batchesQ.data?.total ?? 0} lote(s)
                </span>
              </div>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">
              Lotes em estoque
            </h3>
          </CardHeader>

          {batchesQ.isLoading && (
            <div style={{ padding: '1rem' }}>
              <LoadingSkeleton count={4} height="40px" />
            </div>
          )}

          {!batchesQ.isLoading && batchRows.length === 0 && (
            <div className="track-result track-empty">
              <p>Nenhum lote registrado.</p>
            </div>
          )}

          {!batchesQ.isLoading && batchRows.length > 0 && (
            <div className="track-table">
              <table>
                <thead>
                  <tr>
                    <th>Lote</th>
                    <th>Produto</th>
                    <th>Fabricação</th>
                    <th>Validade</th>
                    <th style={{ textAlign: 'right' }}>Quantidade</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {batchRows.map((b) => (
                    <tr key={b.id}>
                      <td className="font-label-md text-label-md">{b.batchNumber}</td>
                      <td>{productName(b.productId)}</td>
                      <td>{fmtDate(b.manufacturingDate)}</td>
                      <td>{fmtDate(b.expiryDate)}</td>
                      <td
                        style={{
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        }}
                      >
                        {b.quantity}
                      </td>
                      <td>
                        <Badge variant={STATUS_VARIANT[b.status] ?? 'neutral'} size="sm">
                          {STATUS_LABEL[b.status] ?? b.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'batches' && (
        <Card>
          <CardHeader
            action={
              <Button size="sm" onClick={() => setBatchModalOpen(true)}>
                + Registrar Lote
              </Button>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Lotes</h3>
          </CardHeader>
          <CardBody>
            <div className="track-search">
              <Input
                label=""
                placeholder="Buscar por número de lote..."
                value={batchSearch}
                onChange={(e) => setBatchSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchBatch(); }}
              />
              <Button onClick={handleSearchBatch} disabled={!batchSearch.trim()}>
                Buscar
              </Button>
            </div>

            {/* Batch tracking results */}
            {searchedBatch && batchLoading && (
              <div style={{ marginTop: '1rem' }}>
                <LoadingSkeleton count={4} height="32px" />
              </div>
            )}

            {searchedBatch && batchError && (
              <div className="track-result track-error" role="alert">
                <p>Lote não encontrado ou erro na consulta</p>
                <p className="state-detail">{(batchError as Error).message}</p>
              </div>
            )}

            {searchedBatch && !batchLoading && !batchError && !batchTracking && (
              <div className="track-result track-empty">
                <p>Nenhum resultado encontrado para o lote informado.</p>
              </div>
            )}

            {batchTracking && !batchLoading && (
              <div className="track-details" style={{ marginTop: '1rem' }}>
                <div className="track-info-grid">
                  <div className="track-info-item">
                    <span className="track-label">Produto</span>
                    <span className="track-value">{productName(batchTracking.batch.productId)}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Lote</span>
                    <span className="track-value">{batchTracking.batch.batchNumber}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Quantidade</span>
                    <span className="track-value">{batchTracking.batch.quantity}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Fabricação</span>
                    <span className="track-value">{fmtDate(batchTracking.batch.manufacturingDate)}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Validade</span>
                    <span className="track-value">{fmtDate(batchTracking.batch.expiryDate)}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Status</span>
                    <span className="track-value">
                      <Badge variant={STATUS_VARIANT[batchTracking.batch.status] ?? 'neutral'} size="sm">
                        {STATUS_LABEL[batchTracking.batch.status] ?? batchTracking.batch.status}
                      </Badge>
                    </span>
                  </div>
                </div>

                {/* Histórico real: movimentos filtrados pelo número do lote. */}
                <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.875rem', color: '#191c1e' }}>
                  Movimentações do lote
                </h4>
                {lotMovements.length > 0 ? (
                  <div className="track-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Tipo</th>
                          <th style={{ textAlign: 'right' }}>Quantidade</th>
                          <th>Documento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lotMovements.map((m) => (
                          <tr key={m.id}>
                            <td>{new Date(m.createdAt).toLocaleDateString('pt-BR')}</td>
                            <td>
                              <Badge
                                variant={
                                  MOVEMENT_INBOUND.has(m.type)
                                    ? 'success'
                                    : MOVEMENT_OUTBOUND.has(m.type)
                                      ? 'error'
                                      : 'info'
                                }
                                size="sm"
                              >
                                {MOVEMENT_LABEL[m.type] ?? m.type}
                              </Badge>
                            </td>
                            <td
                              style={{
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                                fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                              }}
                            >
                              {m.quantity}
                            </td>
                            <td className="font-label-md text-label-md">{m.reference}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p style={{ marginTop: '0.5rem', color: '#74777f', fontSize: '0.875rem' }}>
                    Nenhuma movimentação registrada para este lote.
                  </p>
                )}

                {batchTracking.serialNumbers.length > 0 && (
                  <>
                    <h4 style={{ margin: '1rem 0 0.5rem', fontSize: '0.875rem', color: '#191c1e' }}>
                      Séries geradas ({batchTracking.serialNumbers.length})
                    </h4>
                    <div className="track-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Número de série</th>
                            <th>Status</th>
                            <th>Vendida em</th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchTracking.serialNumbers.map((sn) => (
                            <tr key={sn.id}>
                              <td className="font-label-md text-label-md">{sn.serialNumber}</td>
                              <td>{SERIAL_LABEL[sn.status] ?? sn.status}</td>
                              <td>{fmtDate(sn.soldAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* ── Séries Tab ── */}
      {activeTab === 'serials' && (
        <Card>
          <CardHeader
            action={
              <Button size="sm" onClick={() => setSerialModalOpen(true)}>
                + Registrar Série
              </Button>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Números de Série</h3>
          </CardHeader>
          <CardBody>
            <div className="track-search">
              <Input
                label=""
                placeholder="Buscar por número de série..."
                value={serialSearch}
                onChange={(e) => setSerialSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSerial(); }}
              />
              <Button onClick={handleSearchSerial} disabled={!serialSearch.trim()}>
                Buscar
              </Button>
            </div>

            {searchedSerial && serialLoading && (
              <div style={{ marginTop: '1rem' }}>
                <LoadingSkeleton count={4} height="32px" />
              </div>
            )}

            {searchedSerial && serialError && (
              <div className="track-result track-error" role="alert">
                <p>Série não encontrada ou erro na consulta</p>
                <p className="state-detail">{(serialError as Error).message}</p>
              </div>
            )}

            {searchedSerial && !serialLoading && !serialError && !serialTracking && (
              <div className="track-result track-empty">
                <p>Nenhum resultado encontrado para a série informada.</p>
              </div>
            )}

            {serialTracking && !serialLoading && (
              <div className="track-details" style={{ marginTop: '1rem' }}>
                <div className="track-info-grid">
                  <div className="track-info-item">
                    <span className="track-label">Produto</span>
                    <span className="track-value">{productName(serialTracking.serial.productId)}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Nº Série</span>
                    <span className="track-value">{serialTracking.serial.serialNumber}</span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Status</span>
                    <span className="track-value">
                      <Badge
                        variant={
                          serialTracking.serial.status === 'AVAILABLE'
                            ? 'success'
                            : serialTracking.serial.status === 'SOLD'
                              ? 'error'
                              : 'warning'
                        }
                        size="sm"
                      >
                        {SERIAL_LABEL[serialTracking.serial.status] ?? serialTracking.serial.status}
                      </Badge>
                    </span>
                  </div>
                  <div className="track-info-item">
                    <span className="track-label">Lote de origem</span>
                    <span className="track-value">
                      {serialTracking.batch ? serialTracking.batch.batchNumber : '\u2014'}
                    </span>
                  </div>
                  {serialTracking.serial.soldAt && (
                    <div className="track-info-item">
                      <span className="track-label">Vendida em</span>
                      <span className="track-value">{fmtDate(serialTracking.serial.soldAt)}</span>
                    </div>
                  )}
                  {serialTracking.serial.soldTo && (
                    <div className="track-info-item">
                      <span className="track-label">Vendida para</span>
                      <span className="track-value">{serialTracking.serial.soldTo}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* ── Register Batch Modal ── */}
      <Modal
        isOpen={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        title="Registrar Lote"
        footer={
          <>
            <Button variant="ghost" onClick={() => setBatchModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateBatch} loading={createBatch.isPending}>
              Registrar
            </Button>
          </>
        }
      >
        <div className="track-form">
          <Input
            label="ID do Produto"
            placeholder="Ex: prod-123"
            value={batchForm.productId}
            onChange={(e) => setBatchForm((prev) => ({ ...prev, productId: e.target.value }))}
          />
          <Input
            label="Número do Lote"
            placeholder="Ex: LOTE-001"
            value={batchForm.batchNumber}
            onChange={(e) => setBatchForm((prev) => ({ ...prev, batchNumber: e.target.value }))}
          />
          <Input
            label="Data de Validade (opcional)"
            type="date"
            value={batchForm.expirationDate}
            onChange={(e) => setBatchForm((prev) => ({ ...prev, expirationDate: e.target.value }))}
          />
        </div>
      </Modal>

      {/* ── Register Serial Modal ── */}
      <Modal
        isOpen={serialModalOpen}
        onClose={() => setSerialModalOpen(false)}
        title="Registrar Número de Série"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSerialModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateSerial} loading={createSerial.isPending}>
              Registrar
            </Button>
          </>
        }
      >
        <div className="track-form">
          <Input
            label="ID do Produto"
            placeholder="Ex: prod-123"
            value={serialForm.productId}
            onChange={(e) => setSerialForm((prev) => ({ ...prev, productId: e.target.value }))}
          />
          <Input
            label="Número de Série"
            placeholder="Ex: SN-001"
            value={serialForm.serialNumber}
            onChange={(e) => setSerialForm((prev) => ({ ...prev, serialNumber: e.target.value }))}
          />
        </div>
      </Modal>

      <style>{`
        .track-page { padding: 1.5rem; }
        .track-search { display: flex; gap: 0.5rem; align-items: flex-start; }
        .track-result { text-align: center; padding: 2rem; margin-top: 1rem; }
        .track-error p:first-child { color: #ba1a1a; font-weight: 600; margin: 0; }
        .track-error .state-detail { color: #74777f; font-size: 0.875rem; margin-top: 0.5rem; }
        .track-empty p { color: #74777f; margin: 0; }
        .track-info-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 0.75rem; }
        .track-info-item { display: flex; flex-direction: column; gap: 0.25rem; }
        .track-label { font-size: 12px; color: #43474e; text-transform: uppercase; letter-spacing: 0.02em; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500;}
        .track-value { font-size: 0.875rem; color: #191c1e; font-weight: 600; }
        .track-table { overflow-x: auto; margin-top: 0.5rem; }
        .track-table table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .track-table th { padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; position: sticky; top: 0; z-index: 1;}
        .track-table td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .track-table tbody tr:hover td { background: #eceef0; }
        .track-form { display: flex; flex-direction: column; gap: 1rem; }
      `}</style>
    </div>
  );
}
